import type { CSSProperties, MouseEvent, PointerEvent, ReactElement } from "react";
import {
  getItemDef,
  type EquipmentRecord,
  type EquipSlot,
  type Inventory,
  type PlacedItem,
} from "@rietz/shared";
import { tileDropOutline, type DragState, type ItemSource } from "./drag.js";
import { TileBody } from "./tile.js";

/**
 * The doll: seven slots over a silhouette. ONE component draws two — the
 * player's (`EquipmentDoll`, wells are drop targets with hotkeys) and a
 * corpse's (`BodyDoll`, take-only: no `data-drop-slot`, which is what makes a
 * body slot unnameable as a destination, no highlight, no hotkeys). Never
 * covered by the search (`search.ts`).
 */

/** Tarkov-style layout over the existing authoritative equipment slots. */
interface SlotLayout {
  slot: EquipSlot;
  label: string;
  hotkey?: string;
  gridArea: string;
  justifySelf?: CSSProperties["justifySelf"];
}

const SLOT_LAYOUT: readonly SlotLayout[] = [
  { slot: "helmet", label: "Headwear", gridArea: "helmet", justifySelf: "center" },
  { slot: "rig", label: "Tactical rig", gridArea: "rig" },
  { slot: "vest", label: "Body armor", gridArea: "vest" },
  { slot: "backpack", label: "Backpack", gridArea: "backpack" },
  { slot: "weapon", label: "On sling", hotkey: "2", gridArea: "weapon1" },
  { slot: "pistol", label: "Holster", hotkey: "1", gridArea: "pistol" },
  { slot: "weapon2", label: "On back", hotkey: "3", gridArea: "weapon2" },
] as const;

const SLOT_ACCESSIBLE_NAMES: Record<EquipSlot, string> = {
  pistol: "Pistol weapon slot 1",
  weapon: "Main Weapon slot 2",
  weapon2: "Main Weapon slot 3",
  vest: "Body armor slot",
  helmet: "Headwear slot",
  rig: "Tactical rig slot",
  backpack: "Backpack slot",
};

/** The five tile gestures, as every grid and doll takes them. */
export interface DollTileHandlers {
  onTilePointerDown: (ev: PointerEvent<HTMLDivElement>, placed: PlacedItem, from: ItemSource) => void;
  onTileContextMenu: (ev: MouseEvent<HTMLDivElement>, placed: PlacedItem, from: ItemSource) => void;
  onTileDoubleClick: (ev: MouseEvent<HTMLDivElement>, placed: PlacedItem, from: ItemSource) => void;
  onTileHover: (ev: PointerEvent<HTMLDivElement>, placed: PlacedItem, from: ItemSource) => void;
  onTileLeave: () => void;
}

export interface EquipmentDollProps extends DollTileHandlers {
  inv: Inventory;
  drag: DragState | null;
}

export interface BodyDollProps extends DollTileHandlers {
  /** The dead player's worn slots, straight off `containerState`. */
  equipment: EquipmentRecord;
  drag: DragState | null;
}

/** What the shared layout needs to know beyond the gear itself. */
interface DollProps extends DollTileHandlers {
  equipment: EquipmentRecord;
  drag: DragState | null;
  /** Which kind of source a tile in slot `slot` drags from. */
  sourceFor: (slot: EquipSlot) => ItemSource;
  /** Whether things may be put INTO these slots: drop attribute, hover outline
   *  and hotkey labels all hang off this one answer. */
  droppable: boolean;
  /** Prepended to every slot's accessible name, e.g. `"Body: "`. */
  labelPrefix?: string;
}

function BodySilhouette(): ReactElement {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 180 360"
      style={{
        position: "absolute",
        zIndex: 0,
        left: "50%",
        top: 20,
        width: "48%",
        height: "calc(100% - 28px)",
        transform: "translateX(-50%)",
        color: "rgba(151, 158, 158, 0.18)",
        filter: "drop-shadow(0 8px 12px rgba(0, 0, 0, 0.55))",
        pointerEvents: "none",
      }}
    >
      <circle cx="90" cy="34" r="25" fill="currentColor" />
      <path
        fill="currentColor"
        d="M56 69c8-10 19-15 34-15s27 5 35 15l13 78-18 91-8 112H91l-7-103-8 103H55L42 238l-16-91 13-78 17-8Zm-17 13L17 161 1 235l19 5 24-70 11-60Zm86 0-16 28 11 60 24 70 19-5-16-74-22-79Z"
      />
    </svg>
  );
}

function EquipmentSlot(props: DollProps & { layout: SlotLayout }): ReactElement {
  const { drag, layout, droppable } = props;
  const item = props.equipment[layout.slot];
  const def = item === null ? null : getItemDef(item.defId);
  const source: ItemSource = props.sourceFor(layout.slot);
  const hover =
    droppable && drag !== null && drag.hover?.t === "slot" && drag.hover.slot === layout.slot
      ? drag.hover
      : null;

  return (
    <div
      className={`equipment-slot equipment-slot-${layout.slot}`}
      aria-label={`${props.labelPrefix ?? ""}${SLOT_ACCESSIBLE_NAMES[layout.slot]}`}
      style={{ gridArea: layout.gridArea, justifySelf: layout.justifySelf }}
    >
      <div className="equipment-slot-label">
        <span>{layout.label}</span>
        {layout.hotkey === undefined || !droppable ? null : <kbd>{layout.hotkey}</kbd>}
      </div>
      <div
        className={`equipment-slot-well${item === null ? " equipment-slot-empty" : ""}`}
        // A body's wells carry NO drop attribute: `zoneAt` finds slots through
        // this selector alone.
        {...(droppable ? { "data-drop-slot": layout.slot } : {})}
        onPointerDown={
          item === null
            ? undefined
            : (ev) => props.onTilePointerDown(ev, { item, x: 0, y: 0, rot: 0 }, source)
        }
        onContextMenu={
          item === null
            ? undefined
            : (ev) => props.onTileContextMenu(ev, { item, x: 0, y: 0, rot: 0 }, source)
        }
        onDoubleClick={
          item === null
            ? undefined
            : (ev) => props.onTileDoubleClick(ev, { item, x: 0, y: 0, rot: 0 }, source)
        }
        onPointerEnter={
          item === null
            ? undefined
            : (ev) => props.onTileHover(ev, { item, x: 0, y: 0, rot: 0 }, source)
        }
        onPointerMove={
          item === null
            ? undefined
            : (ev) => props.onTileHover(ev, { item, x: 0, y: 0, rot: 0 }, source)
        }
        onPointerLeave={props.onTileLeave}
        style={{
          outline:
            hover === null
              ? "none"
              : `2px solid ${
                  hover.ok ? "rgba(140, 220, 148, 0.9)" : "rgba(232, 104, 92, 0.9)"
                }`,
        }}
      >
        {item !== null && def !== null ? (
          <TileBody
            def={def}
            item={item}
            faded={drag !== null && drag.iid === item.iid}
            // A part going on, or rounds going in: the worn gun lights up for
            // both, on the same terms as its tile in a grid would.
            modDrop={tileDropOutline(drag, item.iid)}
            containerDrop={
              drag?.hover?.t === "container" && drag.hover.containerIid === item.iid
                ? drag.hover.ok
                  ? "valid"
                  : "invalid"
                : undefined
            }
          />
        ) : (
          <span className="equipment-slot-watermark">{layout.label}</span>
        )}
      </div>
    </div>
  );
}

function Doll(props: DollProps): ReactElement {
  return (
    <div className="equipment-doll">
      <BodySilhouette />
      {SLOT_LAYOUT.map((layout) => (
        <EquipmentSlot key={layout.slot} {...props} layout={layout} />
      ))}
    </div>
  );
}

/** What the PLAYER is wearing. Drop targets, hotkeys and all. */
export function EquipmentDoll({ inv, ...rest }: EquipmentDollProps): ReactElement {
  return (
    <Doll
      {...rest}
      equipment={inv.equipment}
      sourceFor={(slot) => ({ t: "slot", slot })}
      droppable
    />
  );
}

/** What the CORPSE is wearing. Take-only — see the note at the top. */
export function BodyDoll(props: BodyDollProps): ReactElement {
  return (
    <Doll
      {...props}
      sourceFor={(slot) => ({ t: "bodySlot", slot })}
      droppable={false}
      labelPrefix="Body: "
    />
  );
}
